import { useEffect, useState } from "react";
import { messageBubbleStyles, spacing, buttonStyles, typography, theme, mergeStyles, isMobile, mobileSpacing } from "../styles/theme";
import FlagModal from "./FlagModal";
import { parseCardLinks } from "../utils/parseCardLinks";
import ReactMarkdown from "react-markdown";
import CardLink from "./CardLink";
import { trackUserEvent } from '../analytics';
import ExportDropdown from "./ExportDropdown";
import RuleLink from "./RuleLink";

export default function Message({ message, user, question, messages, messageIndex, userData, conversationTitle }) {
  // Check if this is a user message or bot message
  const isUser = message.sender === 'user';
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFlagClick = () => {
    if (!user) {
        // Not logged in - show prompt
        setShowLoginPrompt(true);
        setTimeout(() => setShowLoginPrompt(false), 3000); // Hide after 3 seconds
        trackUserEvent('flag_attempt_no_auth');
    } else {
        // Logged in - show flag modal
        setShowFlagModal(true);
        trackUserEvent('flag_modal_opened');
    }
  };

  // Preprocess markdown to convert [[Card Name]] to temporary links
  const preprocessText = (text) => {
    // First, convert [[Card Name]] to temporary links
    let processed = text.replace(/\[\[([^\]]+)\]\]/g, (match, cardName) => {
        return `[${cardName}](cardlink://card/${encodeURIComponent(cardName)})`;
    });

    // Then, convert rule citations to temporary links
    processed = processed.replace(/\b(rule\s+)?(\d{3}\.\d{1,3}[a-z]?)\b/gi, (match, rulePrefix, ruleNumber) => {
        return `[${rulePrefix || ''}${ruleNumber}](rulelink://rule/${ruleNumber})`;
    });

    return processed;
  };

  // Custom renderer for ReactMarkdown
  const components = {
    a: (props) => {
        const { href, children } = props;

        if (href && href.startsWith('cardlink://')) {
            const cardName = decodeURIComponent(href.replace('cardlink://card/', ''));
            return <CardLink cardName={cardName} isUserMessage={false} />;
        }

        if (href && href.startsWith('rulelink://')) {
            const ruleNumber = href.replace('rulelink://rule/', '');
            return <RuleLink ruleNumber={ruleNumber} />
        }
        return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
  };

  return (
    <>
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
            marginBottom: spacing.lg,
            ...(isMobile() && {
                marginBottom: mobileSpacing.md,
            })
        }}>
            {/* Message bubble */}
            <div style={
                isUser
                    ? mergeStyles(
                        messageBubbleStyles.user,
                        isMobile() && messageBubbleStyles.userMobile,
                        { textAlign: 'left' }
                    ) : mergeStyles(
                        messageBubbleStyles.bot,
                        isMobile() && messageBubbleStyles.botMobile,
                        { textAlign: 'left' }
                    )
                }
            >
                {isUser ? (
                    // User messages: simple card link parsing
                    parseCardLinks(message.text, true)
                ) : (
                    // Bot messages: markdown + card links
                    <>
                        <div className="markdown-content">
                            {(() => {
                                const processed = preprocessText(message.text);
                                return (
                                    <ReactMarkdown
                                        components={components}
                                        urlTransform={(url) => url}
                                    >
                                        {preprocessText(message.text)}
                                    </ReactMarkdown>
                                );
                            })()}
                        </div>
                    </>
                )}
            </div>

            {/* Flag button - only show for bot messages */}
            {!isUser && (
                <div style={{
                    display: 'flex',
                    gap: spacing.sm,
                    marginTop: spacing.xs,
                    ...(isMobile() && {
                        gap: mobileSpacing.xs,
                        marginTop: mobileSpacing,
                    })
                }}>
                    <button
                        onClick={handleFlagClick}
                        style={mergeStyles(
                            buttonStyles.base,
                            buttonStyles.small,
                            {
                                backgroundColor: 'transparent',
                                color: theme.text.secondary,
                                padding: `${spacing.xs} ${spacing.sm}`,
                                fontSize: typography.fontSize.xs,
                                border: 'none',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                minHeight: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: spacing.xs,
                            },
                            isMobile() && {
                                padding: `${mobileSpacing.xs} ${mobileSpacing.sm}`,
                                fontSize: typography.mobileFontSize.xs,
                                minHeight: '48px',
                            }
                        )}
                    >
                        {/* Show just emoji on very small screens */}
                        {isMobile() && windowWidth < 400 ? '🚩' : '🚩 Flag this answer'}
                    </button>
                    <ExportDropdown
                        messages={messages}
                        currentMessageIndex={messageIndex}
                        userData={userData}
                        conversationTitle={conversationTitle}
                    />
                </div>
            )}

            {/* Login prompt */}
            {showLoginPrompt && (
            <div style={{
                marginTop: spacing.sm,
                padding: spacing.md,
                backgroundColor: theme.status.info,
                color: 'white',
                borderRadius: '6px',
                fontSize: typography.fontSize.sm,
                maxWidth: '70%',
                ...(isMobile() && {
                    marginTop: mobileSpacing.xs,
                    padding: mobileSpacing.md,
                    fontSize: typography.mobileFontSize.xs,
                    maxWidth: '85%',
                    wordWrap: 'break-word',
                })
            }}>
                Please sign in to flag answers
            </div>
            )}
        </div>

        {/* Flag Modal */}
        {showFlagModal && (
            <FlagModal
                question={question}
                botResponse={message.text}
                user={user}
                onClose={() => setShowFlagModal(false)}
            />
        )}
    </>
  );
}